<template>
  <div
    ref="root"
    class="hkl-table"
  >
    <a-table
      ref="table"
      :columns="tableColumns"
      :data-source="sortedList"
      :filtered="false"
      :pagination="false"
      v-bind="$attrs"
      :rowKey="(record, index) => index"
      v-on="$listeners"
    >
      <template
        v-for="column, i in columns"
        :slot="`title-${column.dataIndex}`"
      >
        <div
          :key="`title-${i}`"
          class="hkl-table__header-title"
          :class="{'pointer': !column.disabledSorter}"
          @click="() => updateSorter(column)"
        >
          {{ column.titleRaw === true || !$te(column.title) ? column.title : $t(column.title) }}
          <i
            v-if="Array.isArray(sortObject) && sortObject.length && column.dataIndex === sortObject[0]"
            :key="i"
            :class="sortIconClasses"
          />
        </div>
        <div
          v-if="filterBar"
          :key="`filter-${i}`"
          class="hkl-table__header-filter"
        >
          <component
            :is="getFilterComponentName(column)"
            v-model="queryObject[column.dataIndex]"
            v-bind="getFilterComponentAttrs(column)"
          />
        </div>
      </template>
      <template
        v-for="column, j in columns"
        #[column.dataIndex]="text, record, index"
      >
        <component
          :is="getItemComponentName(record, column, index)"
          :key="getItemComponentKey(record, j)"
          v-bind="getItemComponentAttrs(record, column, index)"
          v-on="getItemComponentListeners(record, column, index)"
        >
          {{ record[column.dataIndex] }}
        </component>
      </template>
    </a-table>
    <HKLLoadMore
      :watchElement="$refs.root"
      v-on="$listeners"
    />
  </div>
</template>
<script>
import lodash from 'lodash'
import { Table } from 'ant-design-vue'
import { getCurrentInstance } from 'vue-demi'
import HKLInput from '@/controls/Input/HKLInput'
import HKLLoadMore from '@/controls/Loading/HKLLoadMore'
import { SORT_DIRECTION } from '@/utils/sortingHelper'
import componentMixin from '../componentMixin'
import { onDefaultFilter, onDefaultSort } from './HKLTable.utils'

const HKLTable = {
  name: 'HKLTable',
  components: {
    HKLInput,
    HKLLoadMore,
    Table,
  },
  mixins: [componentMixin],
  props: {
    /**
     * ```ts
     * interface Column {
     *    dataIndex: string,
     *    title: string,
     *    titleRaw?: boolean,
     *    class?: string,
     *    filterComponent?: string | {
     *      name: string,
     *      attrs?: Object | (column: Column) => Object,
     *    } | Component,
     *    onFilter?: (searchVal: any, source: any) => boolean,
     *    disabledSorter?: boolean
     *    onSorter?: (sortObject: [string, 'asc' | 'desc'?], columns: Columns, list: T[]) => T[]
     *    component?: string | {
     *      name?: string | (value: any, source: any, rowIndex: number) => string  | Component | undefined,
     *      attrs?: Object | (value: any, source: any, rowIndex: number) => Object,
     *    } | Component,
     *    render?: (value: any, source: any, rowIndex: number) => any,
     *    emptyValue?: any,
     * }
     * ```
     * @type {Column[]}
     */
    columns: {
      type: Array,
      required: true,
      default: () => ([]),
      validator: (list) => {
        const keys = ['dataIndex']
        const vals = list.reduce((arr, item) => {
          for (const key of keys) {
            arr.push(item[key])
          }
          return arr
        }, []).filter(Boolean)
        return vals.length === keys.length * list.length
      },
    },
    dataSource: {
      type: Array,
      required: true,
      default: () => ([]),
    },
    sort: {
      type: Array | String,
      default: () => ([]),
    },
    filterBar: {
      type: Boolean,
      default: false,
    },
    defaultQueryObject: {
      type: Object,
      default: () => ({}),
    },
    /**
     * @type {boolean | (queryObject: Object, columns: Columns, list: T[]) => T[]}
     * use custom filter function or boolean
     * if it is `ture`, it will use default filter function
     * if it is `false`, it will return list
     */
    onFilter: {
      type: Boolean | Function,
      default: true,
    },
    /**
     * @type {boolean | (sortObject: [string, 'asc' | 'desc'?], columns: Columns, list: T[]) => T[]}
     * use custom sort function or boolean
     * if it is `ture`, it will use default sort function
     * if it is `false`, it will return list
     */
    onSort: {
      type: Boolean | Function,
      default: true,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const forceUpdateSortObject = (val) => {
      proxy.sortObject = val
    }
    return {
      forceUpdateSortObject,
    }
  },
  data () {
    return {
      styleObject: { },
      queryObject: { },
      sortObject: [],
      /*
       *                  +---------+      +-----------+     +----------+
       *                  | Columns |      |QueryObject|     |SortObject|
       *                  +----+----+      +-----+-----+     +----+-----+
       *                       |                 |                |
       *                       v                 v                v
       * +----------+     +---------+      +-----------+     +----------+
       * |DataSource+---->| RawList +----->| FilterList+---->|SortedList|
       * +----------+     +---------+      +-----------+     +----------+
       */
    }
  },
  methods: {
    getFilterComponentName (column) {
      const fn = this.getComponentNameFn(column.filterComponent)
      return fn() || HKLInput
    },
    getFilterComponentAttrs (column) {
      const DEF_ATTRS = { }
      const fn = this.getComponentAttrsFn(column.filterComponent)
      const attrs = fn(column) || DEF_ATTRS
      return attrs
    },
    getItemComponentName (source, column, rowIndex) {
      const DEFAULT_COMPONENT = 'span'
      const value = source[column.dataIndex]
      const fn = this.getComponentNameFn(column.component)
      try {
        const comp = fn(value, source, rowIndex) || DEFAULT_COMPONENT
        return comp
      } catch (error) {
        console.error(error)
        return DEFAULT_COMPONENT
      }
    },
    getItemComponentKey (source, columnIndex) {
      const key = lodash.get(source, 'raw.id', columnIndex)
      return `content-${key}`
    },
    getItemComponentAttrs (source, column, rowIndex) {
      const DEF_ATTRS = { }
      const value = source[column.dataIndex]
      const fn = this.getComponentAttrsFn(column.component)
      try {
        const extraAttrs = fn(value, source, rowIndex) || DEF_ATTRS
        return Object.assign({ record: source }, extraAttrs)
      } catch (error) {
        console.error(error)
        return DEF_ATTRS
      }
    },
    getItemComponentListeners (source, column, rowIndex) {
      const DEFAULT_LISTENERS = { }
      const value = source[column.dataIndex]
      const fn = this.getComponentListenersFn(column.component)
      try {
        const listeners = fn(value, source, rowIndex) || DEFAULT_LISTENERS
        return listeners
      } catch (error) {
        console.error(error)
        return DEFAULT_LISTENERS
      }
    },
    getItemComponentContent (source, column, rowIndex) {
      const value = source[column.dataIndex]
      const render = typeof column.render === 'function' ? column.render : () => value
      const DEFAULT_CONTENT = column.emptyValue || ''
      try {
        const content = render(value, source, rowIndex)
        return (content === undefined || content === null) ? DEFAULT_CONTENT : content
      } catch (error) {
        console.error(error)
        return DEFAULT_CONTENT
      }
    },
    updateSorter (column) {
      if (column.disabledSorter) return
      let order = SORT_DIRECTION.ASC
      if (!this.sortObject[1] && Array.isArray(this.sort) && this.sort[1]) {
        order = this.sort[1]
      }
      if (this.sortObject[0] === column.dataIndex) {
        order = (!this.sortObject[1] || this.sortObject[1] !== SORT_DIRECTION.DESC) ? SORT_DIRECTION.DESC : SORT_DIRECTION.ASC
      }
      this.sortObject = [column.dataIndex, order]
    },
    scrollToTableTop () {
      // Jump to top line when click search button
      this.$refs.root.scrollTo(0, 0)
    },
  },
  computed: {
    tableColumns () {
      let columns = lodash.cloneDeep(this.columns)
      columns = columns.map((col) => {
        delete col.title
        const dataIndexKebabCase = lodash.kebabCase(col.dataIndex)
        col.slots = {
          ...col.slots,
          title: `title-${col.dataIndex}`,
        }
        if (!col.class) {
          col.class = []
        } else if ((typeof col.class === 'object' && !Array.isArray(col.class)) || typeof col.class === 'string') {
          col.class = [col.class]
        }
        if (Array.isArray(col.class) && !col.class.includes(dataIndexKebabCase)) {
          col.class = [...col.class, dataIndexKebabCase]
        }
        col.scopedSlots = {
          ...col.scopedSlots,
          customRender: col.dataIndex,
        }
        return col
      })

      return columns
    },
    rawList () {
      const list = []
      const length = this.dataSource.length
      for (let i = 0; i < length; i++) {
        const source = this.dataSource[i]
        const obj = { raw: source }
        for (const column of this.columns) {
          obj[column.dataIndex] = this.getItemComponentContent(source, column, i)
        }
        list.push(obj)
      }
      return list
    },
    filteredList () {
      const queryObject = lodash.cloneDeep(this.queryObject)
      if (typeof this.onFilter === 'function') {
        return this.onFilter(queryObject, this.columns, this.rawList)
      } else if (this.onFilter) {
        return onDefaultFilter.call(this, queryObject, this.columns, this.rawList)
      }
      return this.rawList
    },
    sortedList () {
      const sortObject = lodash.cloneDeep(this.sortObject)
      if (typeof this.onSort === 'function') {
        return this.onSort(sortObject, this.columns, this.filteredList)
      } else if (this.onSort) {
        return onDefaultSort.call(this, sortObject, this.columns, this.filteredList)
      } else {
      }
      return this.filteredList
    },
    sortIconClasses () {
      return this.sortObject[1] === SORT_DIRECTION.DESC ? 'triangle-down' : 'triangle-up'
    },
  },
  mounted () {
    this.queryObject = Object.assign({}, this.queryObject, lodash.cloneDeep(this.defaultQueryObject))
    if (typeof this.sort === 'string') {
      this.updateSorter({ dataIndex: this.sort })
    } else if (Array.isArray(this.sort) && this.sort.length) {
      this.updateSorter({ dataIndex: this.sort[0] })
    }
    // Watch values after value initialization
    this.$watch('queryObject', (val) => {
      this.$emit('filter', val)
      this.$emit('change', 'filter', this.filteredList)
    }, { deep: true })
    this.$watch('sortObject', (val) => {
      this.$emit('sort', val)
      this.$emit('change', 'sort', this.sortedList)
    }, { deep: true })
  },
}

export default HKLTable
</script>
<style lang='scss' scoped>
.hkl-table {
  &.non-margin {
    margin: 0;
  }
  margin: 5px 15px;
  border-left: 1px solid #f1f1f1;
  border-right: 1px solid #f1f1f1;
  overflow: auto;
  ::v-deep .ant-table {
    color:#0C3A58;
  }
  &__header-title {
    color: #0C3A58;

    .triangle {
      height: 20px;
      width: 20px;
      border: 1px solid;
    }
    .triangle-up {
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 10px solid #0C3A58;
      display: inline-block;
      margin-left: 5px;
    }
    .triangle-down {
      width: 0;
      height: 0;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 10px solid #0C3A58;
      display: inline-block;
      margin-left: 5px;
    }
  }

  ::v-deep .ant-table-thead th {
    border-top: 1px solid #e0e0e0;
    background-color: #fefefe;
  }
  ::v-deep .ant-table-header-column {
    width: 100%;
  }
  ::v-deep .ant-table-column-title {
    font-weight: bold;
    text-transform: uppercase;
    text-align: left;
    white-space: nowrap;
    & .hkl-table__header-title.pointer {
      cursor: pointer;
    }
  }
  ::v-deep .hkl-table__header-filter {
    margin-left: -16px;
    margin-right: -16px;
    margin-top: 16px;
    padding-left: 16px;
    padding-top: 16px;
    padding-right: 16px;
    border-top: 1px solid #f1f1f1;
    display: flex;
    align-items: flex-start;
    vertical-align: top;
    & > * {
      width: 100%;
    }

    .hkl-input-box {
      margin-bottom: 0;
    }

    .hkl-select {
      display: block;
    }
  }
  ::v-deep table {
    margin-bottom: 0;
    border-radius: 0;
    th, td {
      border-radius: 0 !important;
    }
  }
  ::v-deep .ant-table-placeholder {
    display: none;
  }
  ::v-deep .ant-table-empty .ant-table-body {
    overflow: unset !important;
  }
  ::v-deep .ant-table-row:hover td {
    background-color: initial !important;
  }

  &.lifecycle ::v-deep {
    table, thead, tbody {
      border-width: 0
    }
    // #region clone by table.scss
    thead th, thead td, tfoot th, tfoot td {
        padding: 0.5rem 1.25rem 0.5rem 1.25rem;
        padding: 15px 20px;
    }

    tbody th, tbody td {
      padding: 10px 20px;
    }

    tr {
      color: #0C3A58;
      background-color: #f8f8f8;
      &:nth-child(even) {
        background-color: #fefefe;
      }
    }

    th {
      font-family: Verdana;
      font-size: 14px;
      font-weight: bold;
      color: #0C3A58;
      text-transform: uppercase;
      background-color: #fefefe;
    }

    td {
      font-family: Verdana;
      font-size: 14px;
    }
    // #endregion clone by table.scss
  }

}
</style>
